import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { UrlHelper } from '@/utils/UrlHelper';
import { UserActivityListResponseDto } from '@/features/superAdmin/features/dashboard/types/super-admin-dashboard-dto';
import { useSuperAdminDashboardFilter } from '@/features/superAdmin/features/dashboard/store/useSuperAdminDashboardFilter';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { ApiHookProps } from '@/@types/api.types';

type Props = {
    userId?: string;
} & ApiHookProps;

export const useUserActivityListApi = ({ userId }: Props = {}) => {
    const axiosInstance = useAxiosAuth();

    const { filter } = useSuperAdminDashboardFilter();
    const { startDate, endDate, take, skip, orderBy, orderDirection, activityType, searchText } = filter;

    const {
        data: userActivityListData,
        isLoading: isLoadingUserActivityListData,
        isError: isErrorUserActivityListData,
    } = useQuery({
        queryKey: [
            ApiConfig.queries.superAdmin.dashboard.queryKeys.userActivityList,
            startDate,
            endDate,
            take,
            skip,
            orderBy,
            orderDirection,
            activityType,
            searchText,
            userId
        ],
        queryFn: async (): Promise<UserActivityListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.superAdmin.dashboard.endpoints.userActivityList,
                {
                    startDate: DateTimeHelper.getDateOnly(startDate),
                    endDate: DateTimeHelper.getDateOnly(endDate),
                    take,
                    skip,
                    orderBy,
                    orderDirection,
                    activityType,
                    searchText,
                    userId
                },
            );
            const response = await axiosInstance.get<UserActivityListResponseDto>(url);
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    return {
        userActivityListData,
        isLoadingUserActivityListData,
        isErrorUserActivityListData,
    };
};
